import React, {
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Divider from '@mui/material/Divider';
import {
  AppointmentDTO,
  ReminderCommuniqueRequestDTO,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import Controller from 'core/components/FormController';
import {
  appointmentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  validateEmailIfSet,
  validatePhoneIfSet,
  normalizePhoneNumber,
} from 'pages/Dashboard/utils/helper';
import Button from 'pages/Dashboard/components/Button';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import PhoneInput from 'core/components/PhoneInput';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import {
  useSendAppointmentReminder,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  formatDate,
} from 'utils/date';
import {
  isEmptyString,
} from '@populateui/utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ToolTip from 'core/components/Tooltip';
import {
  useGetPhoneNumberTag,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';

function formatSubmissionDate(date: string) {
  return !isEmptyString(date) ? `Last completed on ${formatDate(date)}` : '';
}

export type AppointmentAction = {
  optionText?: string;
  title: string;
  showFormSelect?: boolean;
  allowFormSelect?: boolean;
  showSendModeSelect?: boolean;
  prefillContact?: boolean;
  communiqueType?: string;
};

export type AppointmentConfirmationParams = {
  appointment?: AppointmentDTO;
  selectedAction?: AppointmentAction;
};

type Props = AppointmentConfirmationParams & {
  onClose: () => void;
};

type FormValues = {
  hpi: boolean;
  medicalHistory: boolean;
  email: string;
  sms: string;
  emailSelected: boolean;
  smsSelected: boolean;
};

const defaultValues: FormValues = {
  hpi: false,
  medicalHistory: false,
  emailSelected: false,
  smsSelected: false,
  email: '',
  sms: '',
};

export default function AppointmentConfirmation({
  onClose,
  appointment,
  selectedAction,
}: Props) {
  const {
    confirmAppointmentCommunique,
    encounterId,
    patient,
  } = appointment ?? {};
  const { intakeSubmissionStatus: intakeStatus } = confirmAppointmentCommunique ?? {};
  const notify = useNotify();
  const { mutateAsync: sendAppointmentReminder } = useSendAppointmentReminder();
  const isNew = isNil(selectedAction?.optionText);
  const isHPICompleted = intakeStatus?.hpiSubmitted;
  const isMedicalHistoryCompleted = intakeStatus?.medicalHistorySubmitted;
  const {
    reasonForVisitSubmissionDate,
    medicalHistorySubmissionDate,
  } = patient?.patientIntakeSubmission ?? {};

  const { consentFormSignedAt } = patient?.patientOrgConsentInfo ?? {};

  const formProps = useForm<FormValues>({
    mode: 'all',
    defaultValues,
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    reset,
    watch,
    setValue,
  } = formProps;
  const [
    hpi,
    medicalHistory,
    emailSelected,
    smsSelected,
    sms,
  ] = watch([
    'hpi',
    'medicalHistory',
    'emailSelected',
    'smsSelected',
    'sms',
  ]);

  const {
    isLoading: isLoadingPhoneNumberDetails,
    phoneTag,
  } = useGetPhoneNumberTag(sms);

  const disabled = (
    (isNew && !hpi && !medicalHistory)
    || (!emailSelected && !smsSelected)
    || !isValid || isSubmitting
    || (smsSelected && isLoadingPhoneNumberDetails)
  );

  const onSubmit = async ({
    hpi,
    medicalHistory,
    emailSelected,
    smsSelected,
    email,
    sms,
  }: FormValues) => {
    try {
      const payload: ReminderCommuniqueRequestDTO = {
        appointmentId: encounterId ?? 0,
        sendForms: {
          hpi: hpi ?? false,
          medicalHistory: medicalHistory ?? false,
        },
        sendTo: {
          email: emailSelected ? email : null,
          sms: smsSelected ? normalizePhoneNumber(sms) : null,
        },
      };
      await sendAppointmentReminder(payload);
      const message = isNew ? 'Appointment Confirmed!' : 'Appointment Reminder Sent!';
      notify({
        variant: 'success',
        message,
      });
      onClose();
      appointmentsEventChannel.emit('onEventUpdated', appointment?.encounterId);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (selectedAction?.prefillContact) {
      reset({
        email: patient?.email ?? '',
        sms: patient?.phoneNumber?.phoneNumberWithAreaCode ?? '',
      });
    }
  }, [
    reset,
    selectedAction?.prefillContact,
    patient?.email,
    patient?.phoneNumber?.phoneNumberWithAreaCode,
  ]);

  useEffect(() => {
    if (isNil(reasonForVisitSubmissionDate)) {
      setValue('hpi', true);
    }
  }, [reasonForVisitSubmissionDate]);

  useEffect(() => {
    if (isNil(medicalHistorySubmissionDate)) {
      setValue('medicalHistory', true);
    }
  }, [medicalHistorySubmissionDate]);

  const medicalHistoryTooltip = useMemo(() => formatSubmissionDate(medicalHistorySubmissionDate ?? ''), [medicalHistorySubmissionDate]);
  const reasonForVisitTooltip = useMemo(() => formatSubmissionDate(reasonForVisitSubmissionDate ?? ''), [reasonForVisitSubmissionDate]);

  return (
    <Dialog
      open
      title={selectedAction?.title}
      onClose={onClose}
      onConfirm={() => { }}
      closeBtnClasses="!top-0"
      confirmText="Create"
      dialogClassName="rounded-lg"
      maxWidth="md"
      disableEscapeKeyDown
      disableBackdropClick
      className="nw-appointment-confirmation w-max"
    >
      <FormProvider {...formProps}>
        {isNew && (
          <h2 className="text-xs">The appointment was created successfully.</h2>
        )}
        <Divider className={clsx({ '!mt-2': !isNew })} />
        {selectedAction?.showFormSelect && (
          <>
            <div className="mt-3.5 flex flex-col gap-form-row mb-5 patient-forms">
              <p>
                <span className="font-bold text-sm">Send forms to the patient</span>
                {!isNil(consentFormSignedAt) && (
                  <span className="text-xs text-gray-500 block py-1">
                    Consent Form Signed on
                    {' '}
                    {formatDate(consentFormSignedAt)}
                  </span>
                )}
              </p>
              <Controller
                control={control}
                name="hpi"
                render={({ field }) => (
                  <div className="flex gap-3 items-center">
                    <Checkbox
                      label="Chief Complaint / HPI"
                      id="send-hpi-form"
                      onChange={field.onChange}
                      checked={field.value}
                      disabled={!selectedAction?.allowFormSelect || isHPICompleted}
                    />
                    {!isNil(reasonForVisitSubmissionDate) && (
                      <ToolTip
                        title={reasonForVisitTooltip}
                      >
                        <CheckCircleIcon className="!text-sm text-green-600 cursor-pointer" />
                      </ToolTip>
                    )}
                  </div>
                )}
              />
              <Controller
                control={control}
                name="medicalHistory"
                render={({ field }) => (
                  <div className="flex gap-3 items-center">
                    <Checkbox
                      label="Demographics, Histories, ROS, Consents"
                      id="send-medical-history-form"
                      onChange={field.onChange}
                      checked={field.value}
                      disabled={!selectedAction?.allowFormSelect || isMedicalHistoryCompleted}
                    />
                    {!isNil(medicalHistorySubmissionDate) && (
                      <ToolTip
                        title={medicalHistoryTooltip}
                      >
                        <CheckCircleIcon className="!text-sm text-green-600 cursor-pointer" />
                      </ToolTip>
                    )}
                  </div>
                )}
              />
            </div>
            <hr />
          </>
        )}
        {selectedAction?.showSendModeSelect && (
          <div className="mt-5 flex flex-col gap-form-row mb-8 patient-forms">
            <h2 className="font-bold text-sm">
              Send via
            </h2>
            <div className="flex flex-row gap-form-field items-center">
              <Controller
                control={control}
                name="emailSelected"
                render={({ field }) => (
                  <Checkbox
                    label="Email"
                    id="send-via-email"
                    onChange={field.onChange}
                    checked={field.value}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                rules={{ required: emailSelected, validate: validateEmailIfSet }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    id="email"
                    className="text-gray-900 w-full text-xs"
                  />
                )}
              />
            </div>
            <div className="flex flex-row gap-form-field items-center">
              <Controller
                control={control}
                name="smsSelected"
                render={({ field }) => (
                  <Checkbox
                    label="Text"
                    id="send-via-sms"
                    onChange={field.onChange}
                    checked={field.value}
                  />
                )}
              />
              <Controller
                control={control}
                name="sms"
                rules={{ required: smsSelected, validate: validatePhoneIfSet }}
                render={({ field }) => (
                  <Wrapper
                    id="cell-phone-number"
                    className="w-full"
                  >
                    <PhoneInput
                      {...field}
                      isLoadingPhoneTag={isLoadingPhoneNumberDetails}
                      phoneTag={phoneTag}
                      id="cell-phone-number"
                      name="cell-phone-number"
                    />
                  </Wrapper>
                )}
              />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-3 m-3 mr-0">
          <Button
            className="nw-confirm !py-1.5 ml-3"
            variant="contained"
            type="button"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={disabled}
          >
            Confirm
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
